import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { updateUserStatus } from '../services/api';

const MediaPreview = ({ file, fileType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isError, setIsError] = useState(false);

  const fullUrl = file ? `${file.filePath}` : '';
  
  const handleOpen = () => {
    setIsOpen(true);
    setIsError(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleError = () => {
    setIsError(true);
  };

  return (
    <>
      <div 
        className="cursor-pointer hover:opacity-80 transition-opacity" 
        onClick={handleOpen}
      >
        {fileType === 'profile-pic' && (
          <div className="h-10 w-10 rounded-full overflow-hidden bg-gray-100">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-500">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
        )}
        {fileType === 'video' && (
          <div className="text-blue-500">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        )}
        {(fileType === 'license-front' ) && (
          <div className="inline-flex items-center text-blue-500 hover:underline">
            <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Front Side
          </div>
        )}
        {(fileType === 'license-back') && (
            <div className="inline-flex items-center text-blue-500 hover:underline">
              <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Back Side
            </div>
        )}
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="relative max-w-4xl max-h-[90vh] overflow-auto bg-white rounded-lg shadow-xl">
            <button 
              onClick={handleClose}
              className="absolute top-2 right-2 z-10 text-gray-500 hover:text-gray-800"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="p-4">
              {isError ? (
                <div className="flex flex-col items-center justify-center py-8 px-4 text-center text-red-500">
                  <svg className="h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-lg font-medium">Failed to load media file</p>
                  <p className="text-sm mt-2">{fullUrl}</p>
                </div>
              ) : (
                <>
                  {fileType === 'video' ? (
                    <video 
                      controls 
                      className="max-h-[70vh] mx-auto" 
                      onError={handleError}
                    >
                      <source src={fullUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img 
                      src={fullUrl} 
                      alt={fileType} 
                      className="max-h-[70vh] mx-auto" 
                      onError={handleError}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const UserTable = ({ users, loading, onRefresh }) => {
  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await updateUserStatus(userId, newStatus);
      toast.success('Genius status updated successfully');
      onRefresh(); // Refresh the user list after successful update
    } catch (error) {
      toast.error('Failed to update genius status');
      console.error('Error updating user status:', error);
    }
  };

  const getFileByType = (files, fileType) => {
    return files?.find(file => file.fileType === fileType);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-gray-500">No users found. Try adjusting your filters.</p>
      </div>
    );
  }

  // User Card component for mobile view
  const UserCard = ({ user, index }) => {
    const profilePic = getFileByType(user.files, 'profile-pic');
    const video = getFileByType(user.files, 'video');
    const licenseFront = getFileByType(user.files, 'license-front');
    const licenseBack = getFileByType(user.files, 'license-back');
    
    return (
      <div className="bg-white rounded-lg shadow mb-4 overflow-hidden">
        <div className="p-4">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-gray-500">{index + 1}</span>
            <div className="flex items-center">
              <button
                onClick={() => handleStatusToggle(user.id, user.active)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  user.active ? 'bg-green-500' : 'bg-gray-200'
                }`}
                role="switch"
                aria-checked={user.active}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    user.active ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="ml-2 text-sm text-gray-500">
                {user.active ? 'Approved' : 'Rejected'}
              </span>
            </div>
          </div>
          
          <div className="mb-3">
            <h3 className="text-sm font-medium text-gray-900">Email</h3>
            <p className="text-sm text-gray-500 break-words">{user.email}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">Member's ID</h3>
              {user.licenseFrontUploaded && user.licenseBackUploaded ? (
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                      <svg className="mr-1 h-3 w-3 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Complete
                    </span>
                  </div>
                  {licenseFront && (
                    <div>
                      <MediaPreview file={licenseFront} fileType="license-front" />
                    </div>
                  )}
                  {licenseBack && (
                    <div>
                      <MediaPreview file={licenseBack} fileType="license-back" />
                    </div>
                  )}
                </div>
              ) : (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <svg className="mr-1 h-3 w-3 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Incomplete
                </span>
              )}
            </div>
            
            <div className="flex items-start space-x-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-1">Profile Picture</h3>
                {user.profilePicUploaded && profilePic ? (
                  <MediaPreview file={profilePic} fileType="profile-pic" />
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-1">Video ID</h3>
                {user.videoUploaded && video ? (
                  <MediaPreview file={video} fileType="video" />
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Desktop view - Table */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No.</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member's ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile Picture</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VIDEO ID</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user, index) => {
              const profilePic = getFileByType(user.files, 'profile-pic');
              const video = getFileByType(user.files, 'video');
              const licenseFront = getFileByType(user.files, 'license-front');
              const licenseBack = getFileByType(user.files, 'license-back');
              
              return (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.email}</div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <button
                        onClick={() => handleStatusToggle(user.id, user.active)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          user.active ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                        role="switch"
                        aria-checked={user.active}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            user.active ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="ml-2 text-sm text-gray-500">
                        {user.active ? 'Approved' : 'Rejected'}
                      </span>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.licenseFrontUploaded && user.licenseBackUploaded ? (
                      <div className="space-y-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="mr-1.5 h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Complete
                        </span>
                        <div className="flex space-x-2 mt-1">
                          {licenseFront && (
                            <div>
                              <MediaPreview file={licenseFront} fileType="license-front" />
                            </div>
                          )}
                          {licenseBack && (
                            <div>
                              <MediaPreview file={licenseBack} fileType="license-back" />
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <svg className="mr-1.5 h-4 w-4 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Incomplete
                      </span>
                    )}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.profilePicUploaded && profilePic ? (
                      <MediaPreview file={profilePic} fileType="profile-pic" />
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.videoUploaded && video ? (
                      <MediaPreview file={video} fileType="video" />
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile view - Cards */}
      <div className="md:hidden">
        {users.map((user, index) => (
          <UserCard key={user.id} user={user} index={index} />
        ))}
      </div>
    </div>
  );
};

export default UserTable; 